import { useCallback, useEffect, useRef, useState } from 'react';
import { KeyboardSensor, PointerSensor } from '@dnd-kit/react';
import type { DragEndEvent } from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { Selection } from 'src/projects/types/Selection';
import { SelectionRepository } from 'src/projects/SelectionRepository.ts';
import {
    NEXT_PAGE_DROP_ZONE_ID,
    PREV_PAGE_DROP_ZONE_ID,
} from 'src/projects/components/CrossPageDropZone.tsx';
import { useDragScroll } from 'src/projects/hooks/useDragScroll.ts';

/**
 * By default dnd-kit only starts a touch drag after a 250ms long-press, which
 * combined with the browser owning the touch gesture makes drag-to-reorder feel
 * broken on mobile. Configure the pointer sensor so touch behaves like the
 * mouse: a small movement anywhere on the row (except interactive controls)
 * starts the drag. Paired with `touch-action: none` on the row (see
 * ProjectPage.scss) so the browser doesn't hijack the gesture for scrolling.
 */
const sensors = [
    PointerSensor.configure({
        activationConstraints(event, source) {
            if (event.pointerType === 'touch') {
                return [new PointerActivationConstraints.Distance({ value: 8 })];
            }
            const { activationConstraints } = PointerSensor.defaults;
            return typeof activationConstraints === 'function'
                ? activationConstraints(event, source)
                : activationConstraints;
        },
    }),
    KeyboardSensor,
];

type Params = {
    selectionRepository: SelectionRepository;
    persistCallback: (selections: Selection[]) => Promise<null>;
    currentPage: number;
    goToPage: (page: number) => void;
    offset: number;
    limit: number;
    count: number;
};

/**
 * Encapsulates the drag-and-drop behaviour of the project selections list: in-page reorder plus
 * the cross-page move via the top/bottom drop zones (see CrossPageDropZone). Returns everything
 * ProjectPage needs to wire up the `DragDropProvider` and the post-drop scroll.
 */
export const useCrossPageDrag = ({
    selectionRepository,
    persistCallback,
    currentPage,
    goToPage,
    offset,
    limit,
    count,
}: Params) => {
    const hasPrevPage = currentPage > 1;
    const hasNextPage = offset + limit < count;

    // Anchors for the post-drop scroll (the app's own chrome, so it works embedded or not).
    const toolbarRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);
    // Auto-scroll the list while dragging when it overflows the screen.
    const rowsRef = useRef<HTMLDivElement>(null);
    const { onDragStart, onDragMove, stop: stopDragScroll } = useDragScroll(rowsRef);

    // After a cross-page move, bring the relevant end of the page into view once we've landed on
    // the target page. Dropping up moves the row to the bottom of the previous page, so we scroll
    // to the paginator (the moved row sits just above it); dropping down moves it to the top of the
    // next page, so we scroll to the toolbar (the moved row sits just below it).
    const [pendingScroll, setPendingScroll] = useState<{ page: number; position: 'top' | 'bottom' } | null>(null);
    // Bumped on every cross-page move to force a clean remount of the whole drag context (keys the
    // DragDropProvider). dnd-kit reorders the DOM in place while dragging (no transforms) and leaves
    // a Feedback "clone" of the dragged row that its manager removes over a drop animation; when we
    // switch pages mid-drop that stale DOM desyncs from React and the clone lingers. Remounting the
    // provider destroys the manager (and its clone) and rebuilds rows from the persisted order.
    const [dndKey, setDndKey] = useState(0);

    useEffect(() => {
        if (pendingScroll === null || pendingScroll.page !== currentPage) {
            return;
        }
        const { position } = pendingScroll;
        setPendingScroll(null);
        // Defer past this commit's paint: the page change and the drag-context remount (dndKey) land
        // in the same commit and briefly reflow the list, which would eat a scroll issued
        // synchronously here. Two frames lands after layout settles.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const target = position === 'bottom' ? actionsRef.current : toolbarRef.current;
            target?.scrollIntoView({
                block: position === 'bottom' ? 'end' : 'start',
                behavior: 'smooth',
            });
        }));
    }, [pendingScroll, currentPage]);

    /**
     * Move the dragged selection to `targetIndex` in the full (unpaginated) order and navigate to
     * the adjacent page. Plain list move: the dragged item is lifted out (the rest of its page
     * closes the gap) and re-inserted at the boundary slot of the neighbouring page, everyone else
     * keeping their relative order. `reorderByNames` over the complete name list applies it.
     */
    const moveAcrossPages = useCallback((
        draggedName: string,
        targetIndex: number,
        targetPage: number,
        position: 'top' | 'bottom',
    ) => {
        const names = selectionRepository.list(selectionRepository.count(), 0).map((s) => s.name);
        const draggedIndex = names.indexOf(draggedName);
        if (draggedIndex === -1 || targetIndex < 0 || targetIndex >= names.length) {
            return;
        }
        const [item] = names.splice(draggedIndex, 1);
        names.splice(targetIndex, 0, item);
        persistCallback(selectionRepository.reorderByNames(names))
        // @TODO handle errors
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
        setPendingScroll({ page: targetPage, position });
        setDndKey((key) => key + 1);
        goToPage(targetPage);
    }, [selectionRepository, persistCallback, goToPage]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        stopDragScroll();
        const draggedName = event.operation.source?.id;
        const target = event.operation.target?.id;

        if (draggedName != null && target === PREV_PAGE_DROP_ZONE_ID) {
            // Move it to the last slot of the previous page, then land there scrolled to the
            // paginator so the moved row is visible.
            moveAcrossPages(String(draggedName), offset - 1, currentPage - 1, 'bottom');
            return;
        }
        if (draggedName != null && target === NEXT_PAGE_DROP_ZONE_ID) {
            // Move it to the first slot of the next page and land there scrolled to the toolbar.
            moveAcrossPages(String(draggedName), offset + limit, currentPage + 1, 'top');
            return;
        }

        const currentNames = selectionRepository.list(limit, offset).map((s) => s.name);
        const nextNames = move(currentNames, event);
        if (nextNames === currentNames) {
            return;
        }
        persistCallback(selectionRepository.reorderByNames(nextNames))
        // @TODO handle errors
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [persistCallback, selectionRepository, offset, currentPage, limit, moveAcrossPages, stopDragScroll]);

    return {
        hasPrevPage,
        hasNextPage,
        toolbarRef,
        actionsRef,
        rowsRef,
        dndKey,
        dndProviderProps: {
            sensors,
            onDragStart,
            onDragMove,
            onDragEnd: handleDragEnd,
        },
    };
};
